import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import Select, { components } from "react-select";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { DEFAULT_WRITE_MODE, DEFAULT_WRITE_MODEL, ENGINE_SETTINGS, MAX_MEMORY_CHARACTERS, WORMHOLE_OUTPUT_CARDS, WORMHOLE_OUTPUT_LENGTHS, WRITE_MODELS } from "../../../../src/shared/constants";
import { pickWormholeOutputLengthIndex } from "../../../../src/shared/wormholeOutputLengthWords";
import useWriteModel from "../../hooks/useWriteModel";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { createSelectStyles, createSelectTheme } from "../../styles/a11y";
import { markWriteDefaultChanged } from "../../utils/userPreferences";
import Help from "../common/Help";
import CreativitySlider from "../CreativitySlider";
import ModalButton from "../ModalButton";
import ModelSelectionModal from "../ModelSelectionModal";
import Toggle from "../Toggle";
import { StyledContainer } from "./styles";

const MORE_SETTINGS_MODEL_OVERLAY_Z_INDEX = 1_000_000_000;
const SECTION_SELECT_MENU_Z_INDEX = 1_000_000_001;

const groupStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "16px",
  width: "100%",
  height: "100%",
  // color:"var(--grey-0)"
};

const themeFunction = createSelectTheme();

const styleObject = createSelectStyles({
  menuList: (base) => ({
    ...base,
    paddingTop: "0px",
    paddingBottom: "0px",
    minHeight: "20vh",
    maxHeight: window.innerHeight - 450,
  }),
});

const sectionSelectStyles = createSelectStyles({
  menuList: (base) => ({
    ...base,
    paddingTop: "0px",
    paddingBottom: "0px",
    minHeight: "20vh",
    maxHeight: window.innerHeight - 450,
  }),
  menuPortal: (base) => ({
    ...base,
    // Radix Dialog sets body { pointer-events: none }; restore hit-testing for
    // the body-portaled menu so options remain clickable.
    pointerEvents: "auto",
    zIndex: SECTION_SELECT_MENU_Z_INDEX,
  }),
});
const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator
      {...props}
      styles={{ paddingTop: 10 }}
    >
      <SVG src="/assets/settings_dropdown_icon.svg" />
    </components.DropdownIndicator>
  );
};

export default function WriteSettings({
  initialWormholeOutputLength, //
  initialWormholeOutputCards,
  initialCreativitySliderPosition,
  initialWriteModel: _initialWriteModel,
  setEditorOptions,
  setSettingsOpen,
  initialMemory,
  setProjectMemory,
  memoryEnabled,
  setMemoryEnabled,
  setModalOpen,
  editorOptions,
  surface = "dropdown",
  includeKeyDetails = true,
}) {
  const subscription = useSudowriteStore((state) => state.subscription);
  const userData = useSudowriteStore((state) => state.userData);
  const setStyleSettingsNestedModalOpen = useSudowriteStore(
    (state) => state.setStyleSettingsNestedModalOpen,
  );
  const wrapperRef = useRef(null);
  const selectRef = useRef(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [isModelSelectionModalOpen, setIsModelSelectionModalOpen] = useState(false);
  const [wormholeOutputLength, setWormholeOutputLength] = useState(initialWormholeOutputLength);
  const [wormholeOutputCards, setWormholeOutputCards] = useState(initialWormholeOutputCards);
  const [creativitySliderPosition, setCreativitySliderPosition] = useState(initialCreativitySliderPosition);
  const [memory, setMemory] = useState(initialMemory);
  const [memoryFocus, setMemoryFocus] = useState(false);
  const [showLengthOptionTip, setShowLengthOptionTip] = useState(false);
  const [isLengthMenuOpen, setIsLengthMenuOpen] = useState(false);
  const backButtonRef = useRef(null);
  const lengthHydratedRef = useRef(false);
  const persistWriteLengthRef = useRef(
    debounce((lengthValue) => {
      const ud = useSudowriteStore.getState().userData;
      if (ud?.fetched) {
        ud.update({ wormholeOutputLength: lengthValue });
      }
    }, 400),
  );
  const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
  const updateEditorOptions = useCallback(
    (patch) => {
      const latestEditorOptions =
        useSudowriteStore.getState().editorOptions || editorOptions || {};
      setEditorOptions({
        ...latestEditorOptions,
        ...patch,
      });
    },
    [editorOptions, setEditorOptions],
  );

  const writeModel = useWriteModel({ editorOptions, initialWriteModel: _initialWriteModel });

  // Keep local Write controls aligned with external store/project props while
  // mounted (More Settings stays open across agentic/store updates). Only sync
  // when the inbound prop actually changes so we don't clobber in-progress edits
  // or write back from hydration alone.
  const prevWormholeOutputCardsRef = useRef(initialWormholeOutputCards);
  const prevWormholeOutputLengthRef = useRef(initialWormholeOutputLength);
  const prevCreativitySliderPositionRef = useRef(initialCreativitySliderPosition);

  useEffect(() => {
    if (initialWormholeOutputCards === prevWormholeOutputCardsRef.current) return;
    prevWormholeOutputCardsRef.current = initialWormholeOutputCards;
    if (initialWormholeOutputCards !== undefined && initialWormholeOutputCards !== null) {
      setWormholeOutputCards(initialWormholeOutputCards);
    }
  }, [initialWormholeOutputCards]);

  useEffect(() => {
    if (initialWormholeOutputLength === prevWormholeOutputLengthRef.current) return;
    prevWormholeOutputLengthRef.current = initialWormholeOutputLength;
    if (
      typeof initialWormholeOutputLength === "number" &&
      Number.isFinite(initialWormholeOutputLength)
    ) {
      setWormholeOutputLength(initialWormholeOutputLength);
    }
  }, [initialWormholeOutputLength]);

  useEffect(() => {
    if (initialCreativitySliderPosition === prevCreativitySliderPositionRef.current) {
      return;
    }
    prevCreativitySliderPositionRef.current = initialCreativitySliderPosition;
    if (
      typeof initialCreativitySliderPosition === "number" &&
      Number.isFinite(initialCreativitySliderPosition)
    ) {
      setCreativitySliderPosition(initialCreativitySliderPosition);
    }
  }, [initialCreativitySliderPosition]);

  const plainOptionLabel = (data) => {
    const content = <div style={groupStyles}>{data.label}</div>;

    if (data.label === "Max words") {
      return (
        <SudoTooltip
          placement="bottom"
          text="Up to ~2,000 words."
        >
          {content}
        </SudoTooltip>
      );
    }

    return content;
  };

  useEffect(() => {
    setModalOpen?.(isModelSelectionModalOpen);
  }, [setModalOpen, isModelSelectionModalOpen]);

  // When Prose Mode opens from More Settings (section surface), tell the parent
  // Radix dialog to ignore outside pointer / Escape that belong to the nested modal.
  useEffect(() => {
    if (surface !== "section") return undefined;
    setStyleSettingsNestedModalOpen?.(isModelSelectionModalOpen);
    return () => {
      setStyleSettingsNestedModalOpen?.(false);
    };
  }, [surface, isModelSelectionModalOpen, setStyleSettingsNestedModalOpen]);

  // Focus the back button when settings panel opens
  useEffect(() => {
    if (surface !== "dropdown") return;
    requestAnimationFrame(() => {
      backButtonRef.current?.focus();
    });
  }, [surface]);

  const toggleFadeEffect = (hideFade = false) => {
    const menuList = selectRef.current?.select?.menuListRef;
    const menu = document.querySelector('.select [class$="menu"]');
    if (!menuList || !menu) return;

    // force start at the top of the menu when opening
    menuList.scrollTop = 0;

    // toggle hiding fade effect class on the conditional that there is no scroll or we want force hide it caused it reached the bottom
    const hasScroll = menuList.scrollHeight > menuList.clientHeight;
    menu.classList.toggle("hide-fade", !hasScroll || hideFade);

    if (!hideFade) {
      let lastScrollTop = 0;

      const handleScroll = () => {
        const st = menuList.scrollTop;
        console.log(st, lastScrollTop);
        if (st < lastScrollTop) {
          // Scrolling up
          menu.classList.remove("hide-fade");
        }
        lastScrollTop = st <= 0 ? 0 : st; // Reset last scroll position
      };

      menuList.addEventListener("scroll", handleScroll);
    }
  };

  // debounce saving memory since user types it
  const saveMemory = useRef(
    debounce((val) => {
      setProjectMemory?.(val);
    }, 500),
  );

  // when memory textarea field changes we save it debounced
  useEffect(() => {
    if (!includeKeyDetails || !setProjectMemory) return;
    saveMemory.current(memory);
  }, [includeKeyDetails, memory, setProjectMemory]);

  // Sync slider from Firestore when editor props lag account defaults (do not write back on open).
  useEffect(() => {
    if (!userData?.fetched || lengthHydratedRef.current) return;
    const persisted =
      userData.data?.wormholeOutputLength !== undefined && userData.data?.wormholeOutputLength !== null
        ? Number(userData.data.wormholeOutputLength)
        : undefined;
    const resolved = pickWormholeOutputLengthIndex(initialWormholeOutputLength, persisted);
    if (typeof resolved === "number" && Number.isFinite(resolved)) {
      setWormholeOutputLength(resolved);
    }
    lengthHydratedRef.current = true;
  }, [userData?.fetched, userData?.data?.wormholeOutputLength, initialWormholeOutputLength]);

  // whether the textarea needs a placeholder
  const showPlaceholder = !memoryFocus && !(memory && memory.length > 0);

  const groupOptions = (options) => {
    const filtered = options.filter((option) => subscription?.usesLedgers || option.value === DEFAULT_WRITE_MODEL);
    const grouped = filtered.reduce((acc, option) => {
      acc[option.group] = acc[option.group] || [];
      acc[option.group].push(option);
      return acc;
    }, {});

    return Object.keys(grouped).map((group) => ({
      label: group,
      options: grouped[group],
    }));
  };

  useEffect(() => {
    if (selectOpen) {
      toggleFadeEffect();
    }
  }, [selectOpen]);

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const selectStyles = surface === "section" ? sectionSelectStyles : styleObject;
  const selectMenuPortalTarget =
    surface === "section" && typeof document !== "undefined" ? document.body : undefined;
  const selectClassNamePrefix = surface === "section" ? "write-settings-select" : undefined;

  return (
    <div ref={wrapperRef}>
      <StyledContainer $surface={surface}>
        {surface === "dropdown" && (
          <>
            <h3>
              <button
                type="button"
                ref={backButtonRef}
                className="menu-back-button use-component-styles"
                onClick={(e) => {
                  e.stopPropagation();
                  setSettingsOpen(false);
                }}
                aria-label="Back to Write menu"
              >
                <SVG
                  width={20}
                  height={20}
                  src="/assets/back-arrow.svg"
                  aria-hidden="true"
                />
              </button>
              Write settings
            </h3>

            <div className="divider"></div>
          </>
        )}

        <div>
          <CreativitySlider
            creativitySliderPosition={creativitySliderPosition}
            setCreativitySliderPosition={(next) => {
              setCreativitySliderPosition(next);
              updateEditorOptions({ creativitySliderPosition: next });
            }}
            ariaLabel="Creativity level"
            ariaValueMin={0}
            ariaValueMax={ENGINE_SETTINGS[DEFAULT_WRITE_MODE.value].length - 1}
            ariaValueNow={creativitySliderPosition >= 0 ? creativitySliderPosition : Math.floor((ENGINE_SETTINGS[DEFAULT_WRITE_MODE.value].length - 1) / 2)}
          />
        </div>

        <div className="divider"></div>

        <div className="length-settings">
          <div
            className="cards-select"
            onClick={stopPropagation}
          >
            <h3>Number of cards</h3>
            <Select
              formatOptionLabel={plainOptionLabel}
              styles={selectStyles}
              theme={themeFunction}
              components={{ DropdownIndicator }}
              className={"select"}
              menuPortalTarget={selectMenuPortalTarget}
              classNamePrefix={selectClassNamePrefix}
              value={WORMHOLE_OUTPUT_CARDS.find((c) => c.value === wormholeOutputCards)}
              isSearchable={false}
              onChange={(selectedOption) => {
                setWormholeOutputCards(selectedOption.value);
                updateEditorOptions({
                  wormholeOutputCards: selectedOption.value,
                });
              }}
              options={WORMHOLE_OUTPUT_CARDS}
              aria-label={
                surface === "section"
                  ? "Write number of cards"
                  : "Number of cards"
              }
            />
          </div>

          <div
            className="length-select"
            onClick={stopPropagation}
          >
            <h3>
              Length of cards{" "}
              <Help aria={"Length of cards - The target length for the AI to write. Depending on the passage and model selected, it may write fewer words."}>
                The target length for the AI to write. Depending on the passage and model selected, it may write fewer words.
              </Help>
            </h3>
            <Select
              formatOptionLabel={plainOptionLabel}
              styles={selectStyles}
              theme={themeFunction}
              components={{ DropdownIndicator }}
              className={"select"}
              menuPortalTarget={selectMenuPortalTarget}
              classNamePrefix={selectClassNamePrefix}
              value={WORMHOLE_OUTPUT_LENGTHS.find((c) => c.value === wormholeOutputLength)}
              isSearchable={false}
              onChange={(selectedOption) => {
                const next = selectedOption.value;
                setWormholeOutputLength(next);
                updateEditorOptions({ wormholeOutputLength: next });
                persistWriteLengthRef.current(next);
              }}
              options={WORMHOLE_OUTPUT_LENGTHS}
              onMenuOpen={() => setIsLengthMenuOpen(true)}
              onMenuClose={() => setIsLengthMenuOpen(false)}
              aria-label="Length of cards"
            />
          </div>
        </div>

        <div className="warning"></div>

        {subscription?.usesLedgers && (
          <>
            <div className="divider"></div>
            <div className="mode-settings">
              <div className="mode-select">
                <h3>Prose Mode</h3>

                <ModelSelectionModal
                  isOpen={isModelSelectionModalOpen}
                  overlayZIndex={
                    surface === "section"
                      ? MORE_SETTINGS_MODEL_OVERLAY_Z_INDEX
                      : undefined
                  }
                  onClose={() => {
                    setIsModelSelectionModalOpen(false);
                  }}
                  selectedModelId={writeModel}
                  onSelectModelId={async (modelId) => {
                    updateEditorOptions({ writeModel: modelId, hasChangedWriteDefault: true });
                    await markWriteDefaultChanged(userData, modelId);
                  }}
                  writeMode={true}
                  supportsBenchmarkMode={true}
                />

                <ModalButton
                  label={
                    WRITE_MODELS.find((c) => c.value === writeModel)?.label === "Basic" && isInUnlimitedExperiment // Temp Unlimited Sudowrite Experiment
                      ? "Unlimited"
                      : WRITE_MODELS.find((c) => c.value === writeModel)?.label
                  }
                  onClick={() => {
                    // Pause the parent popover's close-on-blur in this same
                    // discrete event. The passive effect that syncs modalOpen
                    // runs at default priority under React 18 createRoot, which
                    // is after react-modal steals focus — the blur would close
                    // the menu and unmount this modal with it.
                    setModalOpen(true);
                    setIsModelSelectionModalOpen(true);
                  }}
                  aria-label="Prose Mode"
                />
              </div>
            </div>
          </>
        )}

        {includeKeyDetails && (
          <>
            <div className="divider"></div>

            <div className="memory-enabled-toggle">
              <Toggle
                set={setMemoryEnabled}
                checked={memoryEnabled}
                aria-label="Enable key details so you can add details that Sudowrite should keep in mind about your story"
              />
            </div>
            <h3>Key Details</h3>

            <div className="memory">
              {showPlaceholder && (
                <div className="placeholder">
                  What important details about your story should Sudowrite keep in mind?
                  <br />
                  <br />
                  Example - Lieutenant Stryker is harboring a dark secret.
                  <br />
                  Setting: Mars
                </div>
              )}

              <textarea
                disabled={!memoryEnabled}
                maxLength={MAX_MEMORY_CHARACTERS}
                value={memory}
                onFocus={() => {
                  setMemoryFocus(true);
                }}
                onBlur={() => {
                  setMemoryFocus(false);
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setMemory(value);
                }}
                aria-label="Key Details - Enter important story details for Sudowrite to remember"
              ></textarea>
            </div>

            <p className="disclaimer">Key Details are used with Auto and Guided Write</p>
          </>
        )}
      </StyledContainer>
    </div>
  );
}
